/**
 * Principal Summary Score Page
 *
 * Shows assessment scores for principals from each role:
 * - Teacher assessments (1-5 scale)
 * - Parent assessments (0-2 scale)
 * - Supervisor assessments (1-4 rubric scale)
 * - Student surveys (1-5 scale)
 * - Industry partner assessments (1-5 scale)
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { principalScoreService } from '../services/supabase';

const PrincipalSummary = () => {
  const navigate = useNavigate();
  const { profile, logout } = useAuth();

  const [principals, setPrincipals] = useState([]);
  const [selectedPrincipal, setSelectedPrincipal] = useState(null);
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPrincipals = useCallback(async () => {
    try {
      setLoading(true);
      const { principals: data, error } = await principalScoreService.getPrincipals();

      if (error) {
        setError(error.message);
      } else {
        setPrincipals(data || []);

        // Auto-select first principal if user is principal viewing their own data
        if (profile?.role === 'principal' && profile?.school_id) {
          const ownSchool = data?.find(p => p.school_id === profile.school_id);
          if (ownSchool) {
            setSelectedPrincipal(ownSchool);
          }
        } else if (data && data.length > 0) {
          setSelectedPrincipal(data[0]);
        }
      }
    } catch (err) {
      console.error('Error loading principals:', err);
      setError('Failed to load principals');
    } finally {
      setLoading(false);
    }
  }, [profile?.role, profile?.school_id]);

  useEffect(() => {
    loadPrincipals();
  }, [loadPrincipals]);

  useEffect(() => {
    if (selectedPrincipal) {
      loadPrincipalScores(selectedPrincipal);
    }
    // loadPrincipalScores is intentionally excluded to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPrincipal]);

  const loadPrincipalScores = async (principal) => {
    try {
      setLoading(true);
      const { scores: data, error } = await principalScoreService.getPrincipalScores(
        principal.school_id
      );

      if (error) {
        setError(error.message);
      } else {
        setScores(data);
      }
    } catch (err) {
      console.error('Error loading scores:', err);
      setError('Failed to load scores');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Calculate normalized percentage for each role's scale
  const getNormalizedPercentage = (avgScore, role) => {
    if (avgScore === null || avgScore === undefined) return 0;

    const maxScores = {
      teacher: 5,           // 1-5 scale
      parent: 2,            // 0-2 scale
      supervisor: 4,        // 1-4 scale (PKKS rubric)
      student: 5,           // 1-5 scale
      industry_partner: 5   // 1-5 scale
    };

    const max = maxScores[role] || 5;
    return Math.round((avgScore / max) * 100);
  };

  // Get color based on percentage
  const getScoreColor = (percentage) => {
    if (percentage >= 80) return { bg: 'bg-green-500', text: 'text-green-600', label: 'Sangat Baik' };
    if (percentage >= 60) return { bg: 'bg-blue-500', text: 'text-blue-600', label: 'Baik' };
    if (percentage >= 40) return { bg: 'bg-yellow-500', text: 'text-yellow-600', label: 'Cukup' };
    return { bg: 'bg-red-500', text: 'text-red-600', label: 'Perlu Perbaikan' };
  };

  // Role configurations
  const roleConfigs = {
    teacher: {
      label: 'Guru',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      gradient: 'from-blue-500 to-blue-600',
      scale: 'Skala 1-5'
    },
    parent: {
      label: 'Orang Tua',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      gradient: 'from-cyan-500 to-cyan-600',
      scale: 'Skala 0-2'
    },
    supervisor: {
      label: 'Pengawas',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      gradient: 'from-purple-500 to-purple-600',
      scale: 'Rubrik PKKS'
    },
    student: {
      label: 'Siswa',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
        </svg>
      ),
      gradient: 'from-green-500 to-green-600',
      scale: 'Skala 1-5'
    },
    industry_partner: {
      label: 'Mitra Industri',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      gradient: 'from-orange-500 to-orange-600',
      scale: 'Skala 1-5'
    }
  };

  if (loading && !scores) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Memuat data penilaian...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Ringkasan Penilaian Kepala Sekolah</h1>
              <p className="text-blue-100 mt-1">Rekap skor penilaian dari berbagai pemangku kepentingan</p>
              <p className="text-sm text-blue-200 mt-1">Welcome, {profile?.full_name}</p>
            </div>
            <div className="flex gap-3 items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all font-medium border border-white/30"
              >
                Dashboard
              </button>
              <button
                onClick={handleLogout}
                className="px-6 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Principal Selector */}
        {principals.length > 0 && (
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pilih Kepala Sekolah
            </label>
            <select
              value={selectedPrincipal?.school_id || ''}
              onChange={(e) => {
                const principal = principals.find(p => p.school_id === e.target.value);
                setSelectedPrincipal(principal);
              }}
              className="w-full md:w-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
            >
              {principals.map((p) => (
                <option key={p.school_id} value={p.school_id}>
                  {p.full_name} - {p.school_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Selected Principal Info */}
        {selectedPrincipal && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {selectedPrincipal.full_name?.charAt(0) || 'P'}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{selectedPrincipal.full_name}</h2>
                <p className="text-gray-600">{selectedPrincipal.school_name}</p>
                <p className="text-sm text-gray-500">NPSN: {selectedPrincipal.npsn || '-'}</p>
              </div>
            </div>
          </div>
        )}

        {/* No Data State */}
        {scores && !scores.teacher && !scores.parent && !scores.supervisor && !scores.student && !scores.industry_partner && (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Belum Ada Data Penilaian</h3>
            <p className="text-gray-500">Data penilaian untuk kepala sekolah ini belum tersedia.</p>
          </div>
        )}

        {/* Scores by Role */}
        {scores && (scores.teacher || scores.parent || scores.supervisor || scores.student || scores.industry_partner) && (
          <>
            {/* Overall Summary Card */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg p-6 mb-8 text-white">
              <h3 className="text-lg font-medium opacity-90 mb-4">Ringkasan Skor Keseluruhan</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {Object.entries(roleConfigs).map(([role, config]) => {
                  const roleScore = scores[role];
                  const avgScore = roleScore?.avg_score || 0;
                  const percentage = getNormalizedPercentage(avgScore, role);
                  const colorInfo = getScoreColor(percentage);

                  return (
                    <div key={role} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium">{config.label}</span>
                      </div>
                      <div className="flex items-end gap-2">
                        <span className="text-2xl font-bold">{percentage}%</span>
                        <span className="text-xs opacity-75 mb-0.5">({avgScore.toFixed(1)})</span>
                      </div>
                      <div className="mt-2 h-2 bg-white/20 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${colorInfo.bg}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Detailed Score Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(roleConfigs).map(([role, config]) => {
                const roleScore = scores[role];
                const avgScore = roleScore?.avg_score || 0;
                const totalResponses = roleScore?.total_responses || 0;
                const totalScore = roleScore?.total_score || 0;
                const percentage = getNormalizedPercentage(avgScore, role);
                const colorInfo = getScoreColor(percentage);

                return (
                  <div
                    key={role}
                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    {/* Card Header */}
                    <div className={`bg-gradient-to-r ${config.gradient} text-white p-5`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {config.icon}
                          <div>
                            <h3 className="text-lg font-bold">{config.label}</h3>
                            <p className="text-sm opacity-80">{config.scale}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-3xl font-bold">{avgScore.toFixed(2)}</span>
                          <p className="text-sm opacity-80">Rata-rata</p>
                        </div>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-5">
                      {/* Progress Ring */}
                      <div className="flex justify-center mb-4">
                        <div className="relative w-32 h-32">
                          <svg className="w-32 h-32 transform -rotate-90">
                            <circle
                              cx="64"
                              cy="64"
                              r="56"
                              stroke="#e5e7eb"
                              strokeWidth="12"
                              fill="none"
                            />
                            <circle
                              cx="64"
                              cy="64"
                              r="56"
                              stroke={percentage >= 80 ? '#10b981' : percentage >= 60 ? '#3b82f6' : percentage >= 40 ? '#f59e0b' : '#ef4444'}
                              strokeWidth="12"
                              fill="none"
                              strokeLinecap="round"
                              strokeDasharray={`${(percentage / 100) * 352} 352`}
                              className="transition-all duration-1000"
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                              <span className="text-3xl font-bold text-gray-800">{percentage}%</span>
                              <p className={`text-xs font-medium ${colorInfo.text}`}>{colorInfo.label}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-600">Total Responden</span>
                          <span className="font-semibold text-gray-800">{totalResponses} orang</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-600">Total Skor</span>
                          <span className="font-semibold text-gray-800">{totalScore.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-600">Status</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${colorInfo.bg} text-white`}>
                            {colorInfo.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Assessment Details */}
            <div className="mt-8 bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Detail Penilaian</h3>

              <div className="space-y-6">
                {/* Teacher Details */}
                {scores.teacher?.components && scores.teacher.components.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                      Penilaian Guru
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Kategori</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Rata-rata</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Responden</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scores.teacher.components.map((comp, idx) => (
                            <tr key={idx} className="border-b border-gray-100">
                              <td className="py-2 px-3">{comp.category || '-'}</td>
                              <td className="py-2 px-3 text-right font-medium">{comp.avg_score?.toFixed(2) || '-'}</td>
                              <td className="py-2 px-3 text-right">{comp.count || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Parent Details */}
                {scores.parent?.components && scores.parent.components.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <span className="w-3 h-3 bg-cyan-500 rounded-full"></span>
                      Penilaian Orang Tua
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Komponen</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Rata-rata</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Responden</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scores.parent.components.map((comp, idx) => (
                            <tr key={idx} className="border-b border-gray-100">
                              <td className="py-2 px-3">{comp.component_name}</td>
                              <td className="py-2 px-3 text-right font-medium">{comp.avg_score?.toFixed(2) || '-'}</td>
                              <td className="py-2 px-3 text-right">{comp.total_responses || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Supervisor Details */}
                {scores.supervisor?.components && scores.supervisor.components.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                      Penilaian Pengawas (PKKS Rubric)
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Kompetensi</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Skor</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Level</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scores.supervisor.components.map((comp, idx) => (
                            <tr key={idx} className="border-b border-gray-100">
                              <td className="py-2 px-3">{comp.criterion_name}</td>
                              <td className="py-2 px-3 text-right font-medium">{comp.avg_score?.toFixed(2) || '-'}</td>
                              <td className="py-2 px-3 text-right">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  comp.rating_level === 'excellent' ? 'bg-green-100 text-green-800' :
                                  comp.rating_level === 'very_good' ? 'bg-blue-100 text-blue-800' :
                                  comp.rating_level === 'good' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {comp.rating_level?.replace('_', ' ') || '-'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Student Details */}
                {scores.student?.components && scores.student.components.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                      Survei Siswa
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Kategori</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Rata-rata</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Responden</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scores.student.components.map((comp, idx) => (
                            <tr key={idx} className="border-b border-gray-100">
                              <td className="py-2 px-3">{comp.category || '-'}</td>
                              <td className="py-2 px-3 text-right font-medium">{comp.avg_score?.toFixed(2) || '-'}</td>
                              <td className="py-2 px-3 text-right">{comp.count || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Industry Partner Details */}
                {scores.industry_partner?.components && scores.industry_partner.components.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                      Penilaian Mitra Industri
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Kategori</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Rata-rata</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Responden</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scores.industry_partner.components.map((comp, idx) => (
                            <tr key={idx} className="border-b border-gray-100">
                              <td className="py-2 px-3">{comp.category || '-'}</td>
                              <td className="py-2 px-3 text-right font-medium">{comp.avg_score?.toFixed(2) || '-'}</td>
                              <td className="py-2 px-3 text-right">{comp.count || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PrincipalSummary;
